import { Box, Divider, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import IcomxInlineTelecomAction from 'src/sections/crs/patient/components/icomx/IcomxInlineTelecomAction';
import { IcomxChatContext } from 'src/sections/crs/patient/components/icomx/IcomxChatContext';

export default function IcomxInlineTelecomActionHarness() {
  const [launches, setLaunches] = useState<string[]>([]);

  const contextValue = useMemo(
    () => ({
      isEnabled: true,
      isLoading: false,
      isLaunching: false,
      openResourceConversation: ({
        resourceType,
        resourceId,
      }: {
        resourceType: string;
        resourceId: string;
      }) => {
        setLaunches((currentLaunches) => [
          `${resourceType}/${resourceId}`,
          ...currentLaunches,
        ]);
      },
    }),
    []
  );

  return (
    <IcomxChatContext.Provider value={contextValue}>
      <Stack spacing={3} sx={{ mt: 3, maxWidth: 720 }}>
        <Typography variant="body2" color="text.secondary">
          Debug harness for inline iComx telecom chooser behavior using linked resource candidates only.
        </Typography>

        <Box data-testid="icomx-inline-harness-single">
          <Typography variant="subtitle2">Single linked phone candidate</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="body2">+1 (555) 000-1111</Typography>
            <IcomxInlineTelecomAction
              candidates={[
                {
                  id: 'Practitioner/practitioner-single',
                  label: 'Single Linked Practitioner',
                  resourceType: 'Practitioner',
                  resourceId: 'practitioner-single',
                  firstName: 'Single',
                  lastName: 'Practitioner',
                  phone: '+15550001111',
                  telecomSummary: ['+15550001111'],
                },
              ]}
              system="phone"
              tooltip="Open single telecom in iComx"
              value="+15550001111"
            />
          </Box>
        </Box>

        <Divider />

        <Box data-testid="icomx-inline-harness-multi">
          <Typography variant="subtitle2">Multiple linked phone candidates</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="body2">Choose the correct linked phone contact</Typography>
            <IcomxInlineTelecomAction
              candidates={[
                {
                  id: 'Practitioner/practitioner-alpha',
                  label: 'Dr Alpha',
                  resourceType: 'Practitioner',
                  resourceId: 'practitioner-alpha',
                  firstName: 'Dr',
                  lastName: 'Alpha',
                  phone: '+15550002222',
                  telecomSummary: ['+15550002222'],
                  sourceLabel: 'Task Owner Practitioner',
                },
                {
                  id: 'Organization/organization-beta',
                  label: 'Beta Clinic',
                  resourceType: 'Organization',
                  resourceId: 'organization-beta',
                  firstName: 'Beta',
                  lastName: 'Clinic',
                  phone: '+15550003333',
                  telecomSummary: ['+15550003333'],
                  sourceLabel: 'Task Owner Organization',
                },
                {
                  id: 'TaskOwner/unlinked-legacy',
                  label: 'Unlinked Legacy Contact',
                  resourceType: 'TaskOwner',
                  firstName: 'Legacy',
                  lastName: 'Contact',
                  phone: '+15550004444',
                  telecomSummary: ['+15550004444'],
                },
              ]}
              system="phone"
              tooltip="Open multi telecom in iComx"
              value="+15550002222"
            />
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2">Launch Log</Typography>
          <Stack data-testid="icomx-inline-harness-log" spacing={0.5} sx={{ mt: 1 }}>
            {launches.length ? (
              launches.map((launch) => (
                <Typography key={launch} variant="body2">
                  {launch}
                </Typography>
              ))
            ) : (
              <Typography color="text.secondary" variant="body2">
                No launches yet.
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </IcomxChatContext.Provider>
  );
}
